<script setup lang="ts">
import { FrontTypeEnum, type PaymentProviderType } from "@innoclouds/api";
import { useTranslation } from "i18next-vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import YandexBadge from "#/assets/icons/yandex_badge.svg";
import { useCartStore } from "#/store/cart";

const BADGE_PAYMENT_TIMEOUT = 10;

const { t } = useTranslation();
const storeCart = useCartStore();

const emit = defineEmits<{
	cancel: [];
}>();

const timeLeft = ref(BADGE_PAYMENT_TIMEOUT);
let timer: ReturnType<typeof setInterval> | undefined;

const description = computed(() => {
	if (storeCart.selectedProvider?.description) return storeCart.selectedProvider.description;
	return storeCart.providerTypes?.find((provider: PaymentProviderType) => provider?.front_type === FrontTypeEnum.YANDEX_BADGE)?.description;
});

const title = computed(() => description.value || t(`cart-complete.yandex-badge.title`));

const formattedTime = computed(() => {
	const left = Math.max(timeLeft.value, 0);
	const minutes = Math.floor(left / 60);
	const seconds = left % 60;
	return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
});

const cancelPayment = () => {
	clearInterval(timer);
	emit("cancel");
};

const updateTimer = () => {
	timeLeft.value--;
	if (timeLeft.value <= 0) cancelPayment();
};

onMounted(() => {
	timer = setInterval(updateTimer, 1000);
});

onBeforeUnmount(() => {
	clearInterval(timer);
});
</script>

<template>
	<div class="badge_payment_overlay">
		<div class="badge_payment_panel">
			<span class="badge_payment_grip" />

			<h1 class="badge_payment_title">{{ title }}</h1>
			<p class="badge_payment_subtitle">{{ t(`cart-complete.yandex-badge.subtitle`) }}</p>

			<div class="badge_payment_stage" aria-hidden="true">
				<span class="badge_payment_wave" />
				<span class="badge_payment_wave" />
				<img class="badge_payment_icon" :src="YandexBadge" alt="">
			</div>

			<p class="badge_payment_timer">{{ formattedTime }}</p>
			<p class="badge_payment_waiting">{{ t(`cart-complete.yandex-badge.waiting`) }}</p>

			<button type="button" class="badge_payment_cancel" @click="cancelPayment">
				{{ t(`cart-complete.yandex-badge.button.cancel`) }}
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './cartCompleteYandexBadge.scss';
</style>
